"use client";

import React, { useState, useEffect } from "react";

interface AboutMakerProps {
  storeName?: string;
  description?: string;
  founderQuote?: string;
  banner?: string;
  makerUrl?: string;
  websiteLink?: string;
}

export const AboutMaker: React.FC<AboutMakerProps> = ({
  storeName,
  description,
  founderQuote = "Every product tells the story of a single maker.",
  banner,
  makerUrl,
  websiteLink,
}) => {
  const [open, setOpen] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const storyText =
    description ||
    "What started as a small experiment has grown into a beloved local brand. Every product is crafted with intention — sourced carefully, made in small batches, and signed with the maker's name. No shortcuts. No mass scale. Just products the way they should be made.";

  return (
    <>
      <section className="mt-[clamp(40px,5vw,64px)]">
        <div
          className="relative overflow-hidden rounded-[clamp(16px,2vw,24px)] p-[clamp(32px,5vw,64px)] text-white md:grid md:grid-cols-[1fr_1.1fr] md:items-center"
          style={{
            background:
              "linear-gradient(135deg, #2A3527 0%, var(--chutney-green) 60%, #2A3527 100%)",
            gap: "clamp(24px,4vw,64px)",
          }}
        >
          {/* Noise texture */}
          <div
            className="pointer-events-none absolute inset-0 mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")",
              opacity: 0.05,
            }}
          />

          {/* Left: banner image or gradient placeholder */}
          <div
            className="relative flex items-end justify-center overflow-hidden rounded-2xl p-[clamp(16px,2.5vw,24px)]"
            style={{ aspectRatio: "4 / 5" }}
          >
            {(makerUrl || banner) ? (
              <img
                src={makerUrl || banner}
                alt={storeName ?? "Store banner"}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse at 30% 30%, rgba(255,193,59,0.3), transparent 50%), linear-gradient(135deg, #6B5B3E, #4A3E2A)",
                }}
              />
            )}
            {/* Noise on photo */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                opacity: 0.1,
              }}
            />
            {/* Caption */}
            <p
              className="relative z-10 text-center leading-snug"
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: "clamp(13px,1.6vw,15px)",
                color: "rgba(255,255,255,0.85)",
                textShadow: "0 1px 4px rgba(0,0,0,0.5)",
              }}
            >
              &ldquo;{founderQuote}&rdquo;
            </p>
          </div>

          {/* Right: text */}
          <div className="relative z-10">
            {/* Label */}
            <div
              className="mb-[18px] flex items-center gap-3 font-bold uppercase tracking-[2.4px]"
              style={{ fontSize: 11, color: "var(--turmeric)" }}
            >
              <span style={{ width: 32, height: 1, background: "var(--turmeric)", display: "inline-block" }} />
              Meet the maker
            </div>

            {/* Title */}
            <h2
              className="mb-5 leading-tight"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(32px,5vw,56px)",
                fontWeight: 400,
                letterSpacing: "-1px",
              }}
            >
              A story rooted in{" "}
              <em style={{ fontStyle: "italic", color: "var(--jaipur-pink)" }}>passion,</em>
              <br />
              brewed with{" "}
              <em style={{ fontStyle: "italic", color: "var(--jaipur-pink)" }}>care.</em>
            </h2>

            {/* Body */}
            <p
              className="mb-7 max-w-[480px] leading-relaxed"
              style={{
                fontSize: "clamp(14px,1.7vw,16px)",
                color: "rgba(255,255,255,0.85)",
              }}
            >
              {storyText}
            </p>

            {/* CTA hidden */}
          </div>
        </div>
      </section>

      {/* Story Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-2xl overflow-hidden rounded-t-[24px] sm:rounded-[24px] bg-white"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}
          >
            {/* Banner image */}
            {banner ? (
              <div className="relative h-[220px] w-full overflow-hidden">
                <img
                  src={banner}
                  alt={storeName ?? "Store"}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 60%)" }} />
                {storeName && (
                  <h2
                    className="absolute bottom-4 left-6 font-bold text-white"
                    style={{ fontSize: "clamp(18px,3vw,24px)", textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
                  >
                    {storeName}
                  </h2>
                )}
              </div>
            ) : (
              <div
                className="flex h-[140px] w-full items-end px-6 pb-4"
                style={{ background: "linear-gradient(135deg, #2A3527, var(--chutney-green))" }}
              >
                {storeName && (
                  <h2 className="font-bold text-white" style={{ fontSize: "clamp(18px,3vw,24px)" }}>
                    {storeName}
                  </h2>
                )}
              </div>
            )}

            {/* Content */}
            <div className="px-6 py-6">
              {/* Label */}
              <div
                className="mb-3 flex items-center gap-2 font-bold uppercase tracking-[2px]"
                style={{ fontSize: 10, color: "var(--chutney-green)" }}
              >
                <span style={{ width: 24, height: 1, background: "var(--chutney-green)", display: "inline-block" }} />
                Our Story
              </div>

              <p
                className="leading-relaxed whitespace-pre-line"
                style={{ fontSize: "clamp(14px,1.7vw,16px)", color: "var(--techno-black-70)" }}
              >
                {storyText}
              </p>

              {/* Website link */}
              {websiteLink && (
                <a
                  href={websiteLink.startsWith("http") ? websiteLink : `https://${websiteLink}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition"
                  style={{ background: "var(--chutney-green)", color: "#fff" }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = "0.85")}
                  onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
                >
                  {/* Globe icon */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
                  </svg>
                  Visit our website
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                  </svg>
                </a>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full transition"
              style={{ background: "rgba(0,0,0,0.35)", color: "#fff" }}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
